import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://varahasilks.in';
const SITE_NAME = 'Varaha Silks';
const DEFAULT_DESCRIPTION = 'Premium handcrafted silk sarees, banarasi, kanjivaram, and traditional Indian wear. Shop authentic designer sarees online at Varaha Silks.';
const DEFAULT_IMAGE = `${SITE_URL}/logo1.png`;

/**
 * SEO Component for managing meta tags, Open Graph, Twitter Cards, and structured data
 * @param {Object} props
 * @param {string} props.title - Page title (will be appended to site name)
 * @param {string} props.description - Meta description
 * @param {string} props.image - Open Graph image URL
 * @param {string} props.url - Canonical URL (relative path will be converted to full URL)
 * @param {string} props.type - Open Graph type (website, article, product, etc.)
 * @param {Object} props.product - Product data for product pages (optional)
 * @param {string} props.keywords - Meta keywords (comma-separated)
 * @param {boolean} props.noindex - Whether to prevent indexing
 * @param {Object} props.structuredData - Additional structured data (JSON-LD)
 */
const SEO = ({
  title,
  description = DEFAULT_DESCRIPTION,
  image = DEFAULT_IMAGE,
  url,
  type = 'website',
  product,
  keywords,
  noindex = false,
  structuredData,
}) => {
  // Construct full URL
  const fullUrl = url ? (typeof url === 'string' && url.startsWith('http') ? url : `${SITE_URL}${url}`) : SITE_URL;
  
  // Construct full title
  const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
  
  // Construct full image URL - ensure image is a string
  const imageUrl = image && typeof image === 'string' ? image : DEFAULT_IMAGE;
  const fullImage = imageUrl.startsWith('http') ? imageUrl : `${SITE_URL}${imageUrl}`;

  // Default structured data for organization
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL,
    logo: `${SITE_URL}/logo1.png`,
    description: DEFAULT_DESCRIPTION,
    sameAs: [
      // Add your social media links here when available
      // 'https://www.facebook.com/varahasilks',
      // 'https://www.instagram.com/varahasilks',
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'Customer Service',
      availableLanguage: ['English', 'Hindi'],
    },
  };

  // Product structured data if product is provided
  const productSchema = product ? {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name || title,
    description: product.description || description,
    image: product.images && product.images.length > 0 
      ? product.images
          .filter(img => img && typeof img === 'string')
          .map(img => img.startsWith('http') ? img : `${SITE_URL}${img}`)
      : [fullImage],
    brand: {
      '@type': 'Brand',
      name: SITE_NAME,
    },
    offers: product.price ? {
      '@type': 'Offer',
      price: product.price,
      priceCurrency: 'INR',
      availability: product.inStock !== false 
        ? 'https://schema.org/InStock' 
        : 'https://schema.org/OutOfStock',
      url: fullUrl,
    } : undefined,
    aggregateRating: product.rating ? {
      '@type': 'AggregateRating',
      ratingValue: product.rating,
      reviewCount: product.reviewCount || 0,
    } : undefined,
  } : null;

  // Breadcrumb structured data
  const breadcrumbSchema = url && url !== '/' ? {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: SITE_URL,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: title || 'Page',
        item: fullUrl,
      },
    ],
  } : null;

  // Combine all structured data
  const allStructuredData = [
    organizationSchema,
    ...(productSchema ? [productSchema] : []),
    ...(breadcrumbSchema ? [breadcrumbSchema] : []),
    ...(structuredData ? (Array.isArray(structuredData) ? structuredData : [structuredData]) : []),
  ];

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={fullUrl} />
      
      {/* Robots */}
      {noindex ? (
        <>
          <meta name="robots" content="noindex, nofollow" />
          <meta name="googlebot" content="noindex, nofollow" />
        </>
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_IN" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImage} />

      {/* Additional Meta Tags */}
      <meta name="author" content={SITE_NAME} />
      <meta name="theme-color" content="#ffffff" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content={SITE_NAME} />

      {/* Structured Data (JSON-LD) */}
      {allStructuredData.map((data, index) => (
        <script
          key={index}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
        />
      ))}
    </Helmet>
  );
};

export default SEO;

